import { IBadge } from "@faire/web-api/indigofair/badges/IBadge";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { TransparencyOverlay } from "@faire/web/ui/TransparencyOverlay";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { selectBadge } from "@faire/retailer/components/ProductBadge/badgeUtils";
import { IRoundQuantityPickerProps } from "@faire/retailer/components/RoundQuantityPicker";
import { IRoundQuantityPickerStateProps } from "@faire/retailer/components/RoundQuantityPicker/UseRoundQuantityPickerState";
import { SaveToBoard } from "@faire/retailer/features/RetailerBoards/Components/SaveToBoard";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useSettingPersistQuickAddOnSearchTile } from "@faire/retailer/settings/useSettingPersistQuickAddOnSearchTile";
import { UpdateCartHandler } from "@faire/retailer/stores/domain/CartStore/consts";
import { useMarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

import { ProductTileInfo } from "../ProductTileInfo";
import { TopBadges } from "../TopBadges";

import { ProductTileQuickAdd } from "./__internal__/ProductTileQuickAdd";

export enum PRODUCT_TILE_SURFACE {
  EVALUATE,
  DISCOVERY,
  OTHER,
}
interface ITileOverlayContentProps {
  productTile?: ProductTileInfo;
  disableEventBadge?: boolean;
  updateCartFn?: UpdateCartHandler;
  imageContainerRef?: React.RefObject<HTMLDivElement>;
  onTopLeftBadgeVisible?: (tileBadge?: IBadge) => void;
  renderCollapsableQuickAddButtonProps?: IRoundQuantityPickerProps &
    IRoundQuantityPickerStateProps;
  isSubscriedToBackInStock?: boolean;
  deleteBackInStockNotification?: (productOptionToken: string) => void;
  onHeartClicked?: () => void;
  onSaveToBoard?: (boardToken?: string) => void;
  onRemoveFromBoard?: (boardToken?: string) => void;
  onQuickAdd?: (newQuantity?: number) => void;
  onQuickAddSubmit?: (newQuantity?: number) => void;
  onChooseOption?: () => void;
  onChooseOptionQuantityUpdate?: (newQuantity?: number) => void;
  hideAddToCartAndNotifyMe?: boolean;
  shouldHideSaveToBoard?: boolean;
  productTileSurface?: PRODUCT_TILE_SURFACE;
}

export const TileImageOverlayContent: React.FC<ITileOverlayContentProps> = ({
  productTile,
  disableEventBadge,
  updateCartFn,
  imageContainerRef,
  onTopLeftBadgeVisible,
  renderCollapsableQuickAddButtonProps,
  isSubscriedToBackInStock,
  deleteBackInStockNotification,
  onHeartClicked,
  onSaveToBoard,
  onRemoveFromBoard,
  onQuickAdd,
  onQuickAddSubmit,
  onChooseOption,
  onChooseOptionQuantityUpdate,
  hideAddToCartAndNotifyMe,
  shouldHideSaveToBoard,
  productTileSurface,
}) => {
  const { isDesktopAndAbove } = useViewport();

  const [hasHovered, setHasHovered] = useState(false);

  useEffect(() => {
    if (imageContainerRef?.current && isDesktopAndAbove) {
      const imageContainer = imageContainerRef.current;
      const handleMouseEnter = () => {
        setHasHovered(true);
      };
      imageContainer.addEventListener("mouseenter", handleMouseEnter, {
        once: true,
      });
      return () => {
        imageContainer.removeEventListener("mouseenter", handleMouseEnter);
      };
    }
    return () => {};
  }, [imageContainerRef, isDesktopAndAbove]);

  const marketStore = useMarketStore(["isLive"]);

  const canSaveToBoard = isLoggedInRetailer() && shouldHideSaveToBoard !== true;

  const isSalesPaused =
    productTile?.product?.state === IProduct.State.SALES_PAUSED;

  const topBadge = useMemo(() => {
    const productBadges = productTile?.badge_list?.badges ?? [];
    return selectBadge(productBadges, "TOP");
  }, [productTile?.badge_list?.badges]);

  const hideMarketBadge = disableEventBadge && marketStore.isLive;

  const showEventBadge =
    productTile?.is_highlighted_product_for_promotional_event &&
    !hideMarketBadge;

  const persistQuickAdd =
    useSettingPersistQuickAddOnSearchTile() && isLoggedInRetailer();
  const shouldShowQuickAdd = persistQuickAdd
    ? productTile && !hideAddToCartAndNotifyMe
    : productTile && !hideAddToCartAndNotifyMe && hasHovered;

  return (
    <>
      {isSalesPaused ? <BackorderedOverlay /> : null}
      {productTile?.product && !productTile?.is_locked_view ? (
        <TopBadges
          key="top-badges"
          tileBadge={topBadge}
          showEventBadge={showEventBadge}
          onTopLeftBadgeVisible={onTopLeftBadgeVisible}
          favoriteButton={
            canSaveToBoard ? (
              <SaveToBoard
                data-test-id="favorite-button"
                key={`${productTile.product.token}-favorite`}
                productToken={productTile.product.token}
                productImage={productTile.best_image}
                boardTokens={productTile.product.board_tokens}
                brandToken={productTile.product.brand_token}
                onHeartClicked={onHeartClicked}
                onSaveToBoard={onSaveToBoard}
                onRemoveFromBoard={onRemoveFromBoard}
              />
            ) : null
          }
        />
      ) : null}
      {shouldShowQuickAdd && productTile ? (
        <ProductTileQuickAdd
          productTile={productTile}
          updateCartFn={updateCartFn}
          imageContainerRef={imageContainerRef}
          renderCollapsableQuickAddButtonProps={
            renderCollapsableQuickAddButtonProps
          }
          isSubscriedToBackInStock={isSubscriedToBackInStock}
          deleteBackInStockNotification={deleteBackInStockNotification}
          onQuickAdd={onQuickAdd}
          onQuickAddSubmit={onQuickAddSubmit}
          onChooseOption={onChooseOption}
          onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdate}
          productTileSurface={productTileSurface}
        />
      ) : null}
    </>
  );
};

const BackorderedOverlay = styled(TransparencyOverlay)`
  background: rgba(255, 255, 255, 0.5);
`;
